import { useState, useEffect, useCallback } from 'react';
import { InboxOutlined, EyeOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { Upload, Tooltip } from 'antd';
import type { UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/shared/lib/hooks';
import { useFileUploader } from './useFileUploader';
import type { FileUploaderProps } from './types';
import styles from './FileUploader.module.css';

export const FileUploader = ({
  label,
  description,
  value,
  onChange,
  accept,
  multiple,
  maxCount,
  disabled,
  minSizeMB,
  maxSizeMB,
  type,
  language,
  listType,
  showUploadList = true,
  className,
}: FileUploaderProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const { uploadProps, isLimitReached, maxSizeMBText } = useFileUploader({
    value,
    onChange,
    accept,
    multiple,
    maxCount,
    disabled,
    minSizeMB,
    maxSizeMB,
    type,
    language,
    listType,
    showUploadList,
  });

  const closeActions = useCallback(() => setActiveUid(null), []);

  useEffect(() => {
    if (activeUid === null) return;
    const handler = (e: TouchEvent | MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-image-actions]')) {
        closeActions();
      }
    };
    document.addEventListener('touchstart', handler, { passive: true });
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [activeUid, closeActions]);

  const itemRender: UploadProps['itemRender'] = (originNode, file, _fileList, actions) => {
    if (isDesktop) return originNode;
    if (file.status === 'uploading') return originNode;

    const isActive = activeUid === file.uid;

    const handlePreview = () => {
      const url = file.url || file.thumbUrl;
      if (url) window.open(url, '_blank');
      setActiveUid(null);
    };

    const handleRemove = () => {
      actions.remove();
      setActiveUid(null);
    };

    return (
      <div className={styles.touchCard} data-image-actions>
        <img
          src={file.url || file.thumbUrl}
          alt={file.name}
          className={styles.touchCardImage}
        />
        <button
          className={styles.touchCardMore}
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            setActiveUid(isActive ? null : file.uid);
          }}
        >
          <MoreOutlined />
        </button>
        {isActive && (
          <div className={styles.touchCardOverlay}>
            <Tooltip title={t('common.view')}>
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); handlePreview(); }}
                className={styles.touchCardAction}
              >
                <EyeOutlined style={{ color: 'white', fontSize: '14px' }} />
              </button>
            </Tooltip>
            {!disabled && (
              <Tooltip title={t('common.delete')}>
                <button
                  type='button'
                  onClick={(e) => { e.stopPropagation(); handleRemove(); }}
                  className={styles.touchCardAction}
                >
                  <DeleteOutlined style={{ color: 'white', fontSize: '14px' }} />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.root}`}>
      {label && <div className={styles.label}>{label}</div>}

      <Upload.Dragger
        {...uploadProps}
        itemRender={itemRender}
        className={`${styles.dragger} ${isLimitReached ? styles.draggerHidden : ''} ${className || ''}`}
      >
        <div className={styles.iconWrapper}>
          <InboxOutlined className={styles.icon} />
        </div>

        <div className={styles.textBlock}>
          <div className={styles.title}>{t('fileUploader.title')}</div>
          <div className={styles.description}>
            {description ?? t('fileUploader.defaultDescription', { max: maxSizeMBText })}
          </div>
        </div>
      </Upload.Dragger>
    </div>
  );
};
